<template>
  <v-form ref="form" v-model="valid" @submit.prevent>
    <v-text-field
      v-model="localValue.limit"
      data-testid="edit-customer-account-limit"
      type="number"
      variant="outlined"
      :rules="seatLimitRules"
      :disabled="disable"
      persistent-hint
      :hint="$t('modals.editBackupCustomerAccount.limit.hint')"
      :label="$t('modals.editBackupCustomerAccount.limit.label')"
      :placeholder="$t('modals.editBackupCustomerAccount.limit.placeholder')"
    >
      <template #message="{ message }">
        <span class="d-flex align-center">
          <v-icon size="16" class="mr-1" icon="$infoXs" />
          <span v-html="message" />
        </span>
      </template>
    </v-text-field>

    <div class="subtitle1 mt-6 mb-2">
      {{ $t("modals.editBackupCustomerAccount.selectWorkloads.title") }}
    </div>

    <div class="body2 mb-4" v-html="$t('modals.editBackupCustomerAccount.selectWorkloads.info')" />

    <div
      v-if="hasConnectedWorkloads"
      class="info-block d-flex align-center px-3 mb-4"
      data-testid="edit-customer-account-connected-banner"
    >
      <v-icon size="18" class="mr-2" icon="$warning" />
      <span class="body2">
        {{ $t("modals.editBackupCustomerAccount.selectWorkloads.connectedBanner") }}
      </span>
    </div>

    <div class="workloads-list">
      <div class="workloads-list-item" v-for="workload in workloads" :key="workload">
        <v-checkbox
          v-model="localValue.workloadIds"
          :value="workload"
          :data-testid="`edit-customer-account-workload-${workload}`"
          :disabled="disable || isConnectedWorkload(workload)"
          class="px-4"
          hide-details
          color="primary"
        >
          <template #label>
            <span class="d-flex align-center">
              <img
                v-if="workloadIcons[workload]"
                :src="`/images/app-service-logos/${workloadIcons[workload]}`"
                :alt="$t(`general.keepitWorkloads.${workload}`)"
                class="workload-icon mr-4 ml-3"
              />
              <span class="body2">
                {{ $t(`general.keepitWorkloads.${workload}`) }}
              </span>
            </span>
          </template>
        </v-checkbox>
      </div>
    </div>

    <div
      v-if="showWorkloadsError"
      class="body2 text-error mt-2 d-flex align-center"
      data-testid="edit-customer-account-workloads-error"
    >
      <v-icon size="16" class="mr-1" icon="$warning" />
      {{ $t("modals.editBackupCustomerAccount.selectWorkloads.error") }}
    </div>
  </v-form>
</template>

<script lang="ts">
import { defineComponent, type PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import type { UpdateBackupChildPropertiesPayload } from "@/_store/msp-cloud-backup.module.ts";
import {
  KEEPIT_WORKLOAD_ICONS,
  type BackupChild,
  KeepitWorkload,
} from "@/constants/cloud-backup.ts";
import { isWorkloadConnected } from "@/_helpers/cloud-backup.ts";

interface EditCustomerAccountForm {
  workloadIds: KeepitWorkload[];
  limit: number | string | null;
}

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig<BackupChild>>,
      required: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:localValue", "update:valid"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const form = ref();
    const valid = ref(true);
    const showWorkloadsError = ref(false);

    const localValue = ref<EditCustomerAccountForm>({
      // Every cloud app the account already backs up starts out selected. A workload
      // that is not offered below stays in the array untouched, so it survives the save.
      workloadIds: props.config.item.workloads.map((workload) => workload.name),
      // 0 comes back for an account with no limit, which the field shows as empty.
      limit: getBackupChildLimit(props.config.item) || null,
    });

    // A cloud app that is already connected keeps backing up, so it cannot be cleared
    // here — its checkbox stays checked and disabled, with the reason in the banner above.
    const connectedWorkloads = new Set(
      props.config.item.workloads.filter(isWorkloadConnected).map((workload) => workload.name)
    );

    const isConnectedWorkload = (workload: KeepitWorkload): boolean =>
      connectedWorkloads.has(workload);

    const seatLimitRules = [
      (value: number | string | null | undefined) => {
        if (value === undefined || value === null || `${value}`.trim() === "") {
          return true;
        }
        const numeric = Number(value);
        if (!Number.isInteger(numeric)) {
          return i18n.t("validations.onlyInteger");
        }
        if (numeric < 1) {
          return i18n.t("validations.minNumberAllowed", { n: 1 });
        }
        return true;
      },
    ];

    // The cloud-app list sits outside the v-form rules, so its check runs here too.
    const validate = async () => {
      const validationResult = await form.value?.validate();
      showWorkloadsError.value = !localValue.value.workloadIds.length;

      return Boolean(validationResult?.valid) && !showWorkloadsError.value;
    };

    function getBackupChildLimit(item: BackupChild): number {
      if (!item.workloads.length) {
        return 0;
      }

      const total = item.workloads.reduce((sum, workload) => sum + workload.limit, 0);

      return Math.round(total / item.workloads.length);
    }

    // Immediate so the dialog holds the prefilled payload even when nothing is edited.
    watch(
      localValue,
      ({ workloadIds, limit }) => {
        if (workloadIds.length) {
          showWorkloadsError.value = false;
        }
        const payload: UpdateBackupChildPropertiesPayload = {
          workloadIds,
          // An emptied field means "no limit", which the endpoint takes as 0.
          limit: Number(limit) || 0,
        };
        emit("update:localValue", payload);
      },
      { deep: true, immediate: true }
    );

    watch(valid, (value: boolean | null) => {
      emit("update:valid", value ?? true);
    });

    return {
      form,
      valid,
      localValue,
      showWorkloadsError,
      seatLimitRules,
      validate,
      isConnectedWorkload,
      hasConnectedWorkloads: connectedWorkloads.size > 0,
      workloads: Object.values(KeepitWorkload),
      workloadIcons: KEEPIT_WORKLOAD_ICONS,
    };
  },
});
</script>

<style lang="scss" scoped>
.workloads-list {
  border-radius: 8px !important;
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  max-height: 240px;
  overflow: auto;

  &-item {
    height: 40px;
    margin-top: 8px;
  }
}

.workload-icon {
  width: 35px;
  height: 35px;
  flex: 0 0 auto;
}
</style>
